<script>
import InputLine from '@/components/atoms/inputlib/components/InputLine'
import InputTextarea from '@/components/atoms/inputlib/components/InputTextarea'
import InputPhone from '@/components/atoms/inputlib/components/InputPhone'
import FormValidation from '@/components/atoms/FormValidation'
import getAddress from '@/modules/mixins/getAddress'
import { getGoogleMapsAPI } from 'gmap-vue'

export default {
    name: 'LocationForm',
    components: {
        FormValidation,
        InputPhone,
        InputTextarea,
        InputLine,
    },
    mixins: [getAddress],
    model: {
        prop: 'address',
        event: 'change',
    },
    props: {
        address: {
            type: Object,
            default: () => {},
        },
        scrollId: String,
        newAddress: Boolean,
    },
    data() {
        return {
            placesService: null,
            noResults: null,
            noZip: null,
            formErrors: [],
        }
    },
    computed: {
        google: getGoogleMapsAPI,
        isLoggedIn() {
            return this.$user?.isLoggedIn
        },
        addressValue: {
            get() {
                return this.address
            },
            set(address) {
                this.$emit('change', { ...this.address, ...address })
            },
        },
    },
    async mounted() {
        await this.$gmapApiPromiseLazy()
        const map = new this.google.maps.Map(this.$refs.map)
        this.placesService = new this.google.maps.places.PlacesService(map)
    },
    methods: {
        resetError() {
            this.noResults = null
            this.noZip = null
        },
        verifyStep() {
            this.resetError()
            this.$refs.form.formSubmit()
        },
        onError() {
            if (this.formErrors?.[0] && this.scrollId)
                this.$scrollTo(`#${this.formErrors[0].id}`, {
                    container: `#${this.scrollId}`,
                    offset: 0,
                })
        },
        async next() {
            try {
                if (this.isGB) await this.validateZip(this.address.zip_code)
                const addressQuery = this.getAddressText(this.address)
                this.placesService.findPlaceFromQuery(
                    {
                        query: addressQuery,
                        fields: ['place_id', 'geometry', 'formatted_address'],
                    },
                    (results, status) => {
                        if (status === this.google.maps.places.PlacesServiceStatus.OK) {
                            const result = this.parseAddress(
                                results.find(
                                    (result) => result.formatted_address === addressQuery
                                ) || results[0]
                            )
                            this.addressValue = {
                                place_id: result.place_id,
                                full_address: this.getAddressText(this.address),
                                longitude: result.longitude,
                                latitude: result.latitude,
                                original_longitude: result.longitude,
                                original_latitude: result.latitude,
                            }
                            this.$emit('next')
                        }
                    }
                )
            } catch (e) {
                if (e.code === 'ZERO_RESULTS') {
                    this.noResults = true
                } else if (e === 'ZIP_NOT_FOUND') {
                    this.noZip = this.$t('details.address_warning.zip')
                } else {
                    // eslint-disable-next-line no-console
                    console.error(e)
                }
            }
        },
    },
}
</script>

<template lang="pug">
form-validation#step-1.mt-6(@submit="next" ref="form" @error="onError" :form-errors.sync="formErrors")
    .hidden(ref="map")
    input-textarea(
        v-model="addressValue.sub_address"
        validate="subAddress"
        @input="resetError"
        :maxHeight="150"
        :max="255"
        :placeholder="$t('details.placeholder.sub_address')"
        live resize) {{$t('details.sub_address')}}
    input-line(
        v-model="addressValue.house_number"
        validate="house_number"
        @input="resetError"
        :placeholder="$t('details.placeholder.house_number')"
        live required) {{$t('details.house_number')}}
    input-line(
        v-model="addressValue.street"
        validate="name"
        @input="resetError"
        :max="255"
        :placeholder="$t('details.placeholder.street')"
        live required) {{$t('details.street')}}
    input-line(
        v-model="addressValue.town"
        validate="town"
        @input="resetError"
        :max="255"
        :placeholder="$t('details.placeholder.city')"
        live required) {{$t('details.city')}}
    input-line(
        v-model="addressValue.zip_code"
        validate="zip_code"
        @input="resetError"
        :error="noZip"
        :placeholder="$t('details.placeholder.zip_code')"
        live required) {{$t('details.zip_code')}}
    input-phone(
        v-if="isLoggedIn && newAddress"
        v-model="addressValue.phone"
        type="phone"
        validate="phone"
        @input="resetError"
        :info-text="$t('details.phone_info')"
        live required) {{$t('details.phone')}}
    input-line(
        v-if="isLoggedIn && newAddress"
        v-model="addressValue.name"
        validate="name"
        @input="resetError"
        :max="255"
        :placeholder="$t('details.placeholder.address_name')"
        live required) {{$t('details.address_name')}}
    transition(name="fade")
        span.text-warning(v-if="noResults") {{$t("details.address_warning.no_results")}}
</template>
